// Hooks over shell/resourceRoutes.ts (step B1 of the redesign).
//
// Two ways for a page to use the resource URL:
//
// 1. New pages (step C) read the URL as their state:
//      const route = useResourceRoute('pipelines');
//      route.resourceId          // open pipeline, or null on the index
//      route.open(id)            // push /connect/pipelines/<id>
//      route.tab / route.setTab('health')
//
// 2. Pages that already keep the selection in a store or in local state bind
//    it to the URL without changing how they work:
//      useResourceSelectionBinding({ page, selectedId, select, resolve, describe })
//      useResourceTabBinding({ page, resourceId, tab, setTab, defaultTab })
//    The URL opens the resource on load, back/forward move between resources
//    and tabs, and a selection the user makes is pushed as a new entry.
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useNavigationStore } from '../store/navigationStore';
import {
  RESOURCE_KIND_BY_PAGE, locationFromHistory, locationSnapshot, parseResourceLocation, subscribeLocation,
  writeResource, writeTab, type ResourceLocation,
} from './resourceRoutes';
import { useRecentsStore, type ResourceRef } from './recents';
import { leaveThen } from './leaveGuard';

/** pathname + search, re-read on back/forward and on every write from here. */
export function useLocationSnapshot(): string {
  return useSyncExternalStore(subscribeLocation, locationSnapshot, () => '/');
}

export interface ActiveResourceLocation extends ResourceLocation {
  /** The shell is on this page and the URL is one of its addresses. */
  active: boolean;
}

/** What the address bar says about `page`'s resource and tab. */
export function useResourceLocation(page: string): ActiveResourceLocation {
  const snap = useLocationSnapshot();
  const currentPage = useNavigationStore((s) => s.currentPage);
  const q = snap.indexOf('?');
  const loc = parseResourceLocation(page, q < 0 ? snap : snap.slice(0, q), q < 0 ? '' : snap.slice(q));
  return loc
    ? { ...loc, active: currentPage === page }
    : { page, resourceId: null, view: [], tab: null, active: false };
}

export interface OpenOptions {
  tab?: string | null;
  view?: string[];
  /** Replace the history entry instead of adding one (a redirect, a default). */
  replace?: boolean;
}

export interface ResourceRoute extends ActiveResourceLocation {
  /** Open a resource (null: back to the page's index). */
  open: (resourceId: string | null, opts?: OpenOptions) => void;
  /** Switch the tab of the open resource (null: the default tab). */
  setTab: (tab: string | null, opts?: { replace?: boolean }) => void;
}

/** The URL as the state of a resource page. */
export function useResourceRoute(page: string): ResourceRoute {
  const loc = useResourceLocation(page);
  const open = useCallback((resourceId: string | null, opts: OpenOptions = {}) => {
    leaveThen(() => writeResource(page, resourceId, opts.replace ? 'replace' : 'push', { tab: opts.tab ?? null, view: opts.view }));
  }, [page]);
  const setTab = useCallback((tab: string | null, opts: { replace?: boolean } = {}) => {
    writeTab(page, tab, opts.replace ? 'replace' : 'push');
  }, [page]);
  return { ...loc, open, setTab };
}

/** Open a resource from anywhere (Recientes, search, a notification): switch
 *  the shell to its page and put the resource in the address bar; the page's
 *  binding opens it. */
export function navigateToResource(page: string, resourceId: string, opts: { tab?: string | null } = {}) {
  leaveThen(() => {
    const nav = useNavigationStore.getState();
    if (nav.currentPage !== page) useNavigationStore.setState({ currentPage: page, pendingAppId: null, breadcrumbs: [] });
    writeResource(page, resourceId, 'push', { tab: opts.tab ?? null });
  });
}

// ── Binding an existing selection ──────────────────────────────────────────

export interface SelectionBindingOptions {
  page: string;
  /** Id of the resource the page shows as selected, null for none. */
  selectedId: string | null | undefined;
  /** Select a resource by its canonical id, or clear the selection. */
  select: (id: string | null) => void;
  /** The canonical id an id from the URL names: itself, another id (a name
   *  that resolves to an id), null when it does not exist, undefined while
   *  the list is still loading. Default: the URL id as is. */
  resolve?: (urlId: string) => string | null | undefined;
  /** Name (and color) for Recientes; null while unknown. */
  describe?: (id: string) => Pick<ResourceRef, 'name'> & Partial<Pick<ResourceRef, 'color' | 'kind'>> | null;
  enabled?: boolean;
}

/** Keep a page's selected resource and the URL in step, both ways. */
export function useResourceSelectionBinding(opts: SelectionBindingOptions) {
  const { page, select, resolve, describe, enabled = true } = opts;
  const selectedId = opts.selectedId ?? null;
  const loc = useResourceLocation(page);
  const active = enabled && loc.active;
  const urlId = loc.resourceId;
  const resolved = active && urlId ? (resolve ? resolve(urlId) : urlId) : null;
  const st = useRef({ active: false, url: null as string | null, syncing: false, tried: null as string | null, triedFrom: null as string | null });
  const selectRef = useRef(select);
  selectRef.current = select;

  useEffect(() => {
    const s = st.current;
    if (!active) { s.active = false; s.syncing = false; s.tried = null; return; }
    const first = !s.active;
    if (first || s.url !== urlId) { s.syncing = true; s.tried = null; }
    s.active = true;
    s.url = urlId;

    if (s.syncing) {
      // Back or forward can reach a page with unsaved work unasked; every other
      // exit asked where the user clicked it. On a refusal the browser already
      // moved, so `replace` puts the kept resource back on that entry.
      const stay = () => { s.syncing = false; s.tried = null; s.url = selectedId; writeResource(page, selectedId, 'replace', { tab: loc.tab }); };
      const apply = (exit: () => void) => {
        if (!locationFromHistory()) { exit(); return; }
        leaveThen(exit, stay);
      };
      if (!urlId) {
        s.syncing = false;
        // Arriving on the bare page with a selection kept from before: the URL
        // names it (no new entry). Back to the bare page: clear the selection.
        if (first) { if (selectedId) writeResource(page, selectedId, 'replace'); }
        else if (selectedId) apply(() => selectRef.current(null));
        return;
      }
      if (resolved === undefined) return; // the list is still loading
      if (resolved === null) {
        // Unknown or deleted id: the URL follows what the page shows.
        s.syncing = false;
        writeResource(page, selectedId, 'replace');
        return;
      }
      if (resolved !== urlId) { s.url = resolved; writeResource(page, resolved, 'replace', { tab: loc.tab }); }
      if (selectedId === resolved) { s.syncing = false; s.tried = null; return; }
      if (s.tried !== resolved) {
        s.tried = resolved;
        s.triedFrom = selectedId;
        apply(() => selectRef.current(resolved));
        return;
      }
      // Selected, not applied yet (the state update can land a render after
      // the URL's): keep waiting while the selection is the one we replaced.
      if (selectedId === s.triedFrom) return;
      // The user picked something else meanwhile: theirs wins.
      s.syncing = false;
      s.tried = null;
    }

    // The user selected something else: a new history entry.
    if (selectedId !== (s.url ?? null)) {
      s.url = selectedId;
      writeResource(page, selectedId, 'push');
    }
  }, [active, urlId, resolved, selectedId, page, loc.tab]);

  // Recientes: record what the page shows once it has a name.
  const ref = active && selectedId && describe ? describe(selectedId) : null;
  const name = ref?.name ?? null;
  const color = ref?.color;
  const kind = ref?.kind ?? RESOURCE_KIND_BY_PAGE[page];
  useEffect(() => {
    if (!active || !selectedId || !name || !kind) return;
    useRecentsStore.getState().recordVisit({ kind, id: selectedId, name, page, color });
  }, [active, selectedId, name, color, kind, page]);
}

export interface TabBindingOptions<T extends string> {
  page: string;
  /** Resource the tabs belong to; the binding is idle while the URL names another. */
  resourceId: string | null | undefined;
  tab: T;
  setTab: (tab: T) => void;
  defaultTab: T;
  /** Valid tab ids; a ?tab= outside it opens the default. */
  tabs?: readonly T[];
  enabled?: boolean;
}

/** Keep a resource's open tab and ?tab= in step, both ways. Call it after any
 *  effect of the page that resets the tab when the resource changes. */
export function useResourceTabBinding<T extends string>(opts: TabBindingOptions<T>) {
  const { page, tab, defaultTab, tabs, enabled = true } = opts;
  const resourceId = opts.resourceId ?? null;
  const loc = useResourceLocation(page);
  const on = enabled && loc.active && !!resourceId && loc.resourceId === resourceId;
  const valid = (t: string | null): t is T => !!t && (!tabs || (tabs as readonly string[]).includes(t));
  const urlTab: T = valid(loc.tab) ? loc.tab : defaultTab;
  const st = useRef({ key: null as string | null, urlTab: null as string | null });
  const setRef = useRef(opts.setTab);
  setRef.current = opts.setTab;

  useEffect(() => {
    const s = st.current;
    if (!on) { s.key = null; s.urlTab = null; return; }
    const changed = s.key !== resourceId || s.urlTab !== urlTab;
    s.key = resourceId;
    s.urlTab = urlTab;
    // A resource opened, or back/forward: the URL says which tab.
    if (changed) { if (tab !== urlTab) setRef.current(urlTab); return; }
    // The user switched tabs: a new history entry.
    if (tab !== urlTab) { s.urlTab = tab; writeTab(page, tab, 'push'); }
  }, [on, resourceId, urlTab, tab, page]);
}

/**
 * A click on a sidebar entry. On another page it navigates as always. On the
 * page the shell is already on, with one of its resources open (a pipeline, a
 * run), it goes back to the page's index: the entry is "Pipelines", not
 * "Pipelines, as it was" (BRECHAS 18 and 23). On the index it does nothing.
 */
export function navigateFromRail(page: string) {
  const nav = useNavigationStore.getState();
  if (nav.currentPage === page && typeof window !== 'undefined') {
    const loc = parseResourceLocation(page, window.location.pathname, window.location.search);
    if (loc?.resourceId) { leaveThen(() => writeResource(page, null, 'push')); return; }
  }
  nav.navigateTo(page);
}
